import React, { useCallback, useState } from 'react';
import axios from 'axios';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';

import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import V3OfferTemplateDetailsModal from '../components/V3OfferTemplateDetailsModal';

const ORIGIN = {
  SAVED_TEMPLATE: 'saved_template',
  BLUEPRINT: 'blueprint',
};

const getOrigin = (offer) => {
  if (!offer?.template_id || !offer?.template) return null;
  return offer.template.is_blueprint ? ORIGIN.BLUEPRINT : ORIGIN.SAVED_TEMPLATE;
};

export default function useV3OfferTemplate() {
  const ctx = useV3OfferContext();
  // Resolve context fields with safe fallbacks so the hook can be called
  // unconditionally even from components rendered without a V3OfferProvider
  // (tests render V3OfferDealTermsForm bare). All handlers are inert in that
  // case; the consumer relies on `modified` being false to skip UI that
  // depends on them.
  const team = ctx?.team;
  const offer = ctx?.offer;
  const csrfToken = ctx?.csrfToken;
  const dispatch = ctx?.dispatch;
  const openFtuxModal = ctx?.openFtuxModal;

  const [busy, setBusy] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // saveAsNew* live in context so the form's inline link and OriginCard's
  // chevron menu (each calling useV3OfferTemplate separately) share the same
  // modal state — otherwise the form-side click toggles state nobody renders.
  const saveAsNewOpen = !!ctx?.saveAsNewModalOpen;
  const saveAsNewDraft = ctx?.saveAsNewModalDraft || '';
  const setSaveAsNewOpen = useCallback((open) => {
    dispatch?.({ type: V3_ACTIONS.SET_SAVE_AS_NEW_OPEN, payload: open });
  }, [dispatch]);
  const setSaveAsNewDraft = useCallback((draft) => {
    dispatch?.({ type: V3_ACTIONS.SET_SAVE_AS_NEW_DRAFT, payload: draft });
  }, [dispatch]);

  const origin = getOrigin(offer);
  const modified = origin !== null && offer?.origin_modified;

  const authHeaders = { 'X-CSRF-TOKEN': csrfToken };

  const handleSaveAsNewOpen = useCallback(() => {
    if (busy || !ctx) return;
    setSaveAsNewDraft('');
    setSaveAsNewOpen(true);
  }, [busy, ctx]);

  const handleSaveAsNewSubmit = useCallback(async () => {
    const name = saveAsNewDraft.trim();
    if (!name || busy || !ctx) return;
    setBusy(true);
    try {
      // O-4648: server-authored for every offer, single or multi-artist. The
      // client builder only knew the deal scalars, so templates saved through it
      // carried no terms & conditions, deal terms, expenses or ticket allotments
      // — and applying one silently dropped exactly those.
      const { data: newTemplate } = await axios.post(
        `/teams/${team.id}/offer_templates/v3`,
        { from_v3_offer_id: offer.id, name },
        { headers: authHeaders },
      );
      await axios.patch(
        `/teams/${team.id}/offers/v3/${offer.id}`,
        { v3_offer: { template_id: newTemplate.id } },
        { headers: authHeaders },
      );
      dispatch({
        type: V3_ACTIONS.UPDATE_OFFER,
        payload: {
          template_id: newTemplate.id,
          template: {
            id: newTemplate.id,
            name: newTemplate.name,
            is_blueprint: !!newTemplate.is_blueprint,
            icon_name: newTemplate.icon_name,
          },
          origin_modified: false,
        },
      });
      toast.success(`Saved "${name}" as a new template.`);
      setSaveAsNewOpen(false);
    } catch (e) {
      toast.error("Couldn't save template. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, ctx, saveAsNewDraft, team, offer, dispatch]);

  const handleUpdate = useCallback(async () => {
    if (busy || !ctx || !offer?.template_id) return;
    setBusy(true);
    try {
      await axios.patch(
        `/teams/${team.id}/offer_templates/v3/${offer.template_id}`,
        { from_v3_offer_id: offer.id },
        { headers: authHeaders },
      );
      dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { origin_modified: false } });
      toast.success('Template updated.');
    } catch (e) {
      toast.error("Couldn't update template. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, ctx, team, offer, dispatch]);

  const handleUnlink = useCallback(async () => {
    if (busy || !ctx) return;
    if (!window.confirm('Unlink this offer from its template? Your offer values stay the same.')) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `/teams/${team.id}/offers/v3/${offer.id}`,
        { v3_offer: { template_id: null } },
        { headers: authHeaders },
      );
      dispatch({
        type: V3_ACTIONS.UPDATE_OFFER,
        payload: { template_id: null, template: null, origin_modified: data?.origin_modified ?? false },
      });
      toast.success('Unlinked from template.');
    } catch (e) {
      toast.error("Couldn't unlink template. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, ctx, team, offer, dispatch]);

  const handleViewDetails = useCallback(() => {
    if (!ctx || !offer?.template_id) return;
    setDetailsOpen(true);
  }, [ctx, offer]);

  // Modals require a fully-populated context (team.id + csrfToken at minimum).
  // Test-only renders that stub the context with partial fields skip the
  // modal mount entirely — they don't exercise the modal paths anyway.
  const modals = team?.id ? (
    <>
      <V3OfferTemplateDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        team={team}
        templateId={offer?.template_id}
        isBlueprint={origin === ORIGIN.BLUEPRINT}
        csrfToken={csrfToken}
      />
      <Popup
        open={saveAsNewOpen}
        onClose={() => setSaveAsNewOpen(false)}
        modal
        closeOnDocumentClick={!busy}
        closeOnEscape={!busy}
        contentStyle={{ width: '440px', maxWidth: 'calc(100vw - 16px)', padding: 0, border: 'none', borderRadius: '12px', background: 'var(--sl-bg-primary)', color: 'var(--sl-text-primary)' }}
        className="v3-saveasnew-popup"
      >
        <div className="v3-saveasnew-popup-content v3-saveasnew-container" data-testid="v3-origin-save-as-new-dialog">
          <div className="v3-saveasnew-header">
            <h3 className="v3-saveasnew-title">Save as new template</h3>
          </div>
          <div className="v3-saveasnew-body">
            <label className="v3-saveasnew-input-label">Template name</label>
            <input
              type="text"
              value={saveAsNewDraft}
              onChange={(e) => setSaveAsNewDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsNewSubmit(); }}
              autoFocus
              placeholder="e.g. Bluebird Standard"
              className="v3-saveasnew-input"
              data-testid="v3-origin-save-as-new-input"
            />
          </div>
          <div className="v3-saveasnew-footer">
            <button
              type="button"
              className="v3-origin-button"
              onClick={() => setSaveAsNewOpen(false)}
              disabled={busy}
            >Cancel</button>
            <button
              type="button"
              className="v3-origin-button v3-origin-button--primary"
              onClick={handleSaveAsNewSubmit}
              disabled={busy || !saveAsNewDraft.trim()}
              data-testid="v3-origin-save-as-new-submit"
            >Save template</button>
          </div>
        </div>
      </Popup>
    </>
  ) : null;

  return {
    origin,
    modified,
    busy,
    openFtuxModal,
    handlers: {
      offer,
      handleSaveAsNewOpen,
      handleSaveAsNewSubmit,
      handleUpdate,
      handleUnlink,
      handleViewDetails,
    },
    modals,
  };
}

export { ORIGIN };
